import { Component, computed, ErrorHandler, model, ModelSignal, signal, Signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core'
import { form, required } from '@angular/forms/signals'
import { toSignal } from '@angular/core/rxjs-interop'

import { MatDialog } from '@angular/material/dialog'

import { TuettuKieli } from '@lemon/core/model/common'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { AsiakkaanTilikartta, Kirjanpitotili, PaatilikartanProfiili, PaatilikartanProfiilinOsa, Paatilikartta } from 'app/_jaettu-lemonator/model/kirjanpito'
import { HierarkiaKirjanpitotili, TilikarttaJaettuService } from 'app/_jaettu-lemonator/service/tilikartta-jaettu.service'
import { KirjanpitotiliProfiiliMuokkaaDialog, KirjanpitotiliProfiiliMuokkaaDialogData } from './profiili-kirjanpitotili-muokkaa.dialog'

/** Flat node with expandable and level information */
interface FlatKirjanpitotili {
  expandable: boolean
  name: string
  level: number
  kirjanpitotili: Kirjanpitotili
  profiilinOsa: PaatilikartanProfiilinOsa
  kirjanpitotiliAlkuperainen: Kirjanpitotili
  profiilissaMuutetutTiedot: string
}

@Component({
  selector: '[app-profiili-paatilikartta]',
  templateUrl: './profiili-paatilikartta.component.html',
  styleUrls: ['./profiili-paatilikartta.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaatilikarttaProfiiliComponent {
  loading = true
  flatatytSignal: Signal<FlatKirjanpitotili[]>

  commonErrorSignal: WritableSignal<string> = signal('')
  valittuProfiiliPuuttuuSignal: Signal<string> = signal('')
  valittuProfiiliAvainSignal: ModelSignal<PaatilikartanProfiili['avain']> = model('maksuperusteinen')
  valittuKieliSignal: WritableSignal<TuettuKieli> = signal('fi')
  kieliValinta = form(this.valittuKieliSignal, polku => {
    required(polku)
  })
  profiilitSignal: Signal<PaatilikartanProfiili[]> = signal([])
  private _valittuProfiiliSignal: Signal<PaatilikartanProfiili> = signal(null)

  constructor(
    private errorHandler: ErrorHandler,
    private dialog: MatDialog,
    private tilikarttaService: TilikarttaService,
    private tilikarttaJaettuService: TilikarttaJaettuService,
  ) {
    this.valittuProfiiliPuuttuuSignal = computed(() => (this.valittuProfiiliAvainSignal() ? '' : 'Valitse profiili'))

    const paatilikarttaSignal = toSignal(this.tilikarttaService.paatilikarttaObservable)
    this.profiilitSignal = computed(() => Object.values(paatilikarttaSignal()?.profiilit ?? {}))
    this._valittuProfiiliSignal = computed(() => this._annaPaatilikartanProfiili(this.valittuProfiiliAvainSignal(), paatilikarttaSignal()))

    this.flatatytSignal = computed(() => {
      const profiiliAvain = this.valittuProfiiliAvainSignal()
      const paatilikartta = paatilikarttaSignal()
      const kieli = this.valittuKieliSignal()

      const profiili = this._annaPaatilikartanProfiili(profiiliAvain, paatilikartta)

      // Jos profiili on valittu, yhdistä ne muutokset päätilikarttaan.
      // Tämä onnistuu tyhjällä asiakaskohtaisella tilikartalla, johon
      // merkitään valittu profiili.
      const alkuperaisetTilit = paatilikartta?.tilit ? Object.keys(paatilikartta.tilit).map(key => paatilikartta.tilit[key]) : []
      let tilit: Kirjanpitotili[] = null
      if (profiili?.avain) {
        const asiakkaanTilikartta: AsiakkaanTilikartta = {
          tilit: {},
          yliajotilit: {},
        }
        tilit = this.tilikarttaJaettuService.yhdistaAsiakkaantilikarttaPaatilikarttaanProfiilinAvaimella(paatilikartta, asiakkaanTilikartta, profiili.avain)
      } else {
        tilit = alkuperaisetTilit
      }

      // Make hierarchy flat
      const hierarkia = this.tilikarttaJaettuService.muutaTililistausHierarkiaksi(tilit)
      const kaikki: FlatKirjanpitotili[] = []
      for (const juuri of hierarkia) {
        this.flattaa(juuri, 1, kaikki, kieli)
      }

      // Populoi profiilin osat
      if (profiili?.t) {
        for (const t of kaikki) {
          if (profiili.t[t.kirjanpitotili.numero]) {
            t.profiilinOsa = profiili.t[t.kirjanpitotili.numero]
            let lisaa = false
            let explanation = 'Profiilissa '
            if (t.profiilinOsa.nimi?.fi) {
              if (lisaa) {
                explanation += ','
              }
              explanation += t.profiilinOsa.nimi.fi
              lisaa = true
            }
            if (t.profiilinOsa.nimi?.en) {
              if (lisaa) {
                explanation += ','
              }
              explanation += t.profiilinOsa.nimi.en
              lisaa = true
            }
            if (t.profiilinOsa.piilota) {
              if (lisaa) {
                explanation += ','
              }
              explanation += ' piilotettu'
              lisaa = true
            }
            if (lisaa) {
              t.profiilissaMuutetutTiedot = explanation
            }
          }
        }
      }

      // Täytä alkuperäiset tilit
      for (const alk of alkuperaisetTilit) {
        for (const mod of kaikki) {
          if (alk.numero === mod.kirjanpitotili.numero) {
            mod.kirjanpitotiliAlkuperainen = alk
            break
          }
        }
      }

      this.loading = false

      return kaikki
    })
  }

  private _annaPaatilikartanProfiili(profiilinAvain: string, paatilikartta: Paatilikartta): PaatilikartanProfiili | null {
    if (profiilinAvain && paatilikartta && paatilikartta.profiilit && paatilikartta.profiilit[profiilinAvain]) {
      return paatilikartta.profiilit[profiilinAvain]
    }
    return null
  }

  compareProfiles(o1: PaatilikartanProfiili, o2: PaatilikartanProfiili): boolean {
    if (o1 && o2) {
      // console.log(o1, o2)
      return o1.avain === o2.avain
    }
    if (o1 === null && o2 === null) {
      return true
    }
    return false
  }

  private flattaa(hierarkia: HierarkiaKirjanpitotili, level: number, kaikki: FlatKirjanpitotili[], kieli: TuettuKieli) {
    kaikki.push(this.makeFlat(hierarkia, level, kieli))
    if (hierarkia.lapset) {
      for (const lapsi of hierarkia.lapset) {
        this.flattaa(lapsi, level + 1, kaikki, kieli)
      }
    }
  }

  private makeFlat(node: HierarkiaKirjanpitotili, level: number, kieli: TuettuKieli): FlatKirjanpitotili {
    const nimi = this._annaNimi(node, kieli)
    return {
      expandable: !!node.lapset && node.lapset.length > 0,
      name: node.kirjanpitotili.numero + ' ' + nimi,
      level: level,
      kirjanpitotili: node.kirjanpitotili,
      kirjanpitotiliAlkuperainen: null,
      profiilinOsa: null,
      profiilissaMuutetutTiedot: null,
    }
  }

  private _annaNimi(node: HierarkiaKirjanpitotili, kieli: TuettuKieli): string {
    if (node.kirjanpitotili.localisedName) {
      if (node.kirjanpitotili.localisedName[kieli]) {
        return node.kirjanpitotili.localisedName[kieli]
      }
    }
    return node.kirjanpitotili.nimi
  }

  muokkaa(tili: Kirjanpitotili, profiilinOsa: PaatilikartanProfiilinOsa) {
    const profiili = this._valittuProfiiliSignal()
    if (!profiili) {
      return
    }
    const data: KirjanpitotiliProfiiliMuokkaaDialogData = {
      tili: tili,
      profiilinOsa: profiilinOsa,
      profiili: profiili,
    }
    this.dialog.open(KirjanpitotiliProfiiliMuokkaaDialog, { data: data, autoFocus: false })
  }
}
